import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getNotificationCounts,
  getNotifications,
  listAdminAnnouncements,
  sendAdminAnnouncement,
  type AdminAnnouncementHistoryParams,
  type AdminAnnouncementInput,
  type NotificationTab,
} from '@nomiss/services';

export const notificationQueryKeys = {
  list: (tab: NotificationTab) => ['notifications', 'list', tab] as const,
  counts: ['notifications', 'counts'] as const,
  allLists: ['notifications', 'list'] as const,
  adminAnnouncements: ['admin', 'announcements'] as const,
  adminAnnouncementHistory: (filters: AdminAnnouncementHistoryParams) =>
    [
      ...notificationQueryKeys.adminAnnouncements,
      'history',
      filters,
    ] as const,
};

export function useNotifications(
  tab: NotificationTab,
  accessToken?: string | null,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [...notificationQueryKeys.list(tab), accessToken ?? ''] as const,
    queryFn: ({ pageParam }) =>
      getNotifications({
        tab,
        cursor: pageParam,
        limit: 20,
        accessToken,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
    enabled: enabled && Boolean(accessToken),
  });
}

export function useNotificationCounts(
  accessToken?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...notificationQueryKeys.counts, accessToken ?? ''] as const,
    queryFn: () => getNotificationCounts(accessToken),
    staleTime: 15_000,
    refetchOnMount: 'always',
    enabled: enabled && Boolean(accessToken),
  });
}

export function useAdminAnnouncement() {
  const queryClient = useQueryClient();

  const adminAnnouncementMutation = useMutation({
    mutationFn: (input: AdminAnnouncementInput) => sendAdminAnnouncement(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.adminAnnouncements,
      });
    },
  });

  return { adminAnnouncementMutation };
}

export function useAdminAnnouncementHistory(
  filters: AdminAnnouncementHistoryParams,
  options?: { enabled?: boolean },
) {
  const adminAnnouncementHistoryQuery = useQuery({
    queryKey: notificationQueryKeys.adminAnnouncementHistory(filters),
    queryFn: () => listAdminAnnouncements(filters),
    enabled: options?.enabled ?? true,
  });

  return { adminAnnouncementHistoryQuery };
}
